import { Box, Container, Link, styled, Typography } from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const CookiePolicy = () => {
  return (
    <React.Fragment>
      <PageContainer maxWidth="md">
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
        >
          Cookie Policy
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Last updated: July 18, 2026
        </Typography>

        <Section>
          <Typography variant="h5" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            This Cookie Policy explains how FundsyFi uses cookies and similar
            technologies on our websites and in our products — FundsyFi Circle
            (group savings) and FundsyFi Recover (an AI-assisted
            accounts-receivable and revenue-recovery platform for businesses) —
            collectively, the “Service.” Strictly necessary cookies are always
            active. Where required by law, non-essential cookies (functional,
            analytics, and advertising/targeting) are used only after you
            consent, and you can withdraw consent at any time through our consent
            manager, where offered, or your browser settings. We may also use
            open- and click-tracking in our emails; email opens are not always
            reliable and automated security scanners may create false events. See
            our <Link href="/privacy-policy">Privacy Policy</Link> for more.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            2. What are Cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies are small text files placed on your device when you visit a
            website. They store information about your visit—such as your
            preferences or login status—to enhance your experience on subsequent
            visits.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            3. Types of Cookies We Use
          </Typography>
          <Typography variant="h6" gutterBottom>
            3.1 Necessary Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Essential for core functionality (e.g., authentication, security,
            session management). Without these, parts of the Service may not
            work properly.
          </Typography>
          <Typography variant="h6" gutterBottom>
            3.2 Performance & Analytics Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Collect anonymous data on usage (pages visited, time spent) to help
            us improve performance and user experience.
          </Typography>
          <Typography variant="h6" gutterBottom>
            3.3 Functionality Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Remember your choices (language, display preferences) to provide a
            more personalized experience.
          </Typography>
          <Typography variant="h6" gutterBottom>
            3.4 Advertising & Third-Party Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Used to deliver relevant ads and track campaign performance. May be
            set by our advertising partners.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            4. How We Use Cookies
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Authenticate users securely so you remain logged in.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Maintain session state across pages and visits.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Analyze usage patterns to optimize features and performance.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Personalize content, language, and notifications.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Measure and target advertising campaigns.
              </Typography>
            </li>
          </Box>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            5. Your Cookie Choices
          </Typography>
          <Typography variant="body1" paragraph>
            Most browsers allow you to manage cookies via settings:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Accept or block all cookies.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">Delete specific cookies.</Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph>
            Disabling cookies may limit functionality (you may need to log in
            each visit or lose preference settings).
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            6. Third-Party Cookie Disclosures
          </Typography>
          <Typography variant="body1" paragraph>
            We work with trusted third parties who may set cookies when you use
            the Service:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Stripe – payment processing cookies only.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Google Analytics – performance tracking (anonymous data).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Social Media Platforms – remarketing and social widgets.
              </Typography>
            </li>
          </Box>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            7. Changes to This Cookie Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Cookie Policy periodically. When we make changes,
            we will revise the “Last updated” date above and notify you if the
            changes are material. Continued use after changes constitutes
            acceptance.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            8. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about our use of cookies, please contact us
            at:
          </Typography>
          <Typography variant="body1">
            <Link href="mailto:fundsyfi@fundsyfi.com">
              fundsyfi@fundsyfi.com
            </Link>
          </Typography>
          <Typography variant="body1">Houston, Texas 77085</Typography>
        </Section>
      </PageContainer>
      <Footer />
    </React.Fragment>
  );
};

export default CookiePolicy;
